import React, { useState } from "react";
import { useQuery } from "react-query";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance";
import CustomDatePicker from "../CustomDate/CustomDatePicker";

// ✅ أنواع المصروفات (نفس اللي في CreateNewExpense)
const EXPENSE_TYPES = [
  { value: 0, label: "💼 الراتب", apiName: "Salary" },
  { value: 1, label: "🚗 صيانة السيارة", apiName: "CarMaintenance" },


{ value: 2, label: "🚕 إيجار السيارة", apiName: "CarRental" }   ,   
{ value: 3, label: "🏢 إيجار المخزن", apiName: "WarehouseRental" },   


  { value: 4, label: "📦 أخرى", apiName: "Other" },
];

// ✅ طرق الدفع (نفس اللي في CreateNewExpense)
const PAYMENT_METHODS = [
  { value: 0, label: "💵 نقداً", apiName: "Cash" },
  { value: 1, label: "📱 فودافون كاش", apiName: "VodafoneCash" },
  { value: 2, label: "💳 إنستاباي", apiName: "InstaBay" },
];

// ✅ دالة للحصول على النوع بالعربي مع emoji
const getExpenseTypeLabel = (apiName) => {
  const type = EXPENSE_TYPES.find(t => t.apiName === apiName);
  return type ? type.label : apiName;
};

// ✅ دالة للحصول على طريقة الدفع بالعربي مع emoji
const getPaymentMethodLabel = (apiName) => {
  const method = PAYMENT_METHODS.find(m => m.apiName === apiName);
  return method ? method.label : apiName;
};

export default function ExpensesSummary() {
  const navigate = useNavigate();

  // حالات الفلترة والصفحات
  const [selectedType, setSelectedType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ✅ جلب المصروفات مع الفلترة والصفحات
  const { data: expensesData, isLoading, isError, refetch } = useQuery(
    ["expenses", currentPage, pageSize, selectedType, startDate, endDate],
    async () => {
      const params = new URLSearchParams();
      params.append("pageNumber", currentPage);
      params.append("pageSize", pageSize);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate || startDate);
      if (selectedType !== "") params.append("type", selectedType);

      const { data } = await axiosInstance.get(`/Expense/expenses?${params.toString()}`);
      return data;
    }

  );

  const handleFilter = () => {
    if (!startDate && !endDate && selectedType === "") {
      Swal.fire("تنبيه", "يرجى اختيار فلتر واحد على الأقل", "warning");
      return;
    }
    setCurrentPage(1);
    refetch();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setSelectedType("");
    setCurrentPage(1);
    refetch();
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (expensesData?.totalPages || 1)) return;
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const totalPages = expensesData?.totalPages || 1;
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const currentPageSafe = currentPage;
      if (currentPageSafe <= 3) pages.push(1, 2, 3, 4, '...', totalPages);
      else if (currentPageSafe >= totalPages - 2)
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      else pages.push(1, '...', currentPageSafe - 1, currentPageSafe, currentPageSafe + 1, '...', totalPages);
    }
    return pages;
  };

  if (isLoading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>جاري تحميل البيانات...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <p style={{ color: "#dc2626", fontSize: "18px" }}>فشل تحميل البيانات</p>
        <button onClick={() => refetch()} style={{ ...buttonStyle("#2563eb") }}>إعادة المحاولة</button>
      </div>
    );
  }

  const expenses = expensesData?.data || [];
  const totalCount = expensesData?.totalCount || 0;
  const grandTotals = expensesData?.grandTotals || { totalAmount: 0 };
  const totalPages = expensesData?.totalPages || 1;
  const hasNext = expensesData?.hasNext || false;
  const hasPrevious = expensesData?.hasPrevious || false;

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* الرأس */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            💰 ملخص المصروفات
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>عرض {expenses.length} من أصل {totalCount} مصروف</small>
        </div>
        <button onClick={() => navigate("/create-expense")} style={buttonStyle("#22c55e")}>
          <i className="bi bi-plus-circle me-1"></i> إنشاء مصروف
        </button>
      </div>

      {/* الفلترة */}
      <div className="mb-4" style={filterCardStyle}>
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label style={labelStyle}>نوع المصروف</label>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} style={inputStyle}>
              <option value="">كل الأنواع</option>
              {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="col-md-2">
            <CustomDatePicker
              label="تاريخ البداية"
              value={startDate}
              onChange={setStartDate}
              placeholder="اختر تاريخ البداية"
              maxDate={endDate ? new Date(endDate) : null}
            />
          </div>
          
          <div className="col-md-2">
            <CustomDatePicker
              label="تاريخ النهاية"
              value={endDate}
              onChange={setEndDate}
              placeholder="اختر تاريخ النهاية"
              minDate={startDate ? new Date(startDate) : null}
            />
          </div>

          <div className="col-md-2">
            <label style={labelStyle}>عدد الصفوف لكل صفحة</label>
            <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} style={inputStyle}>
              {[5,10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          
          <div className="col-md-3 d-flex gap-2">
            <button onClick={handleFilter} style={buttonStyle("#2563eb")}>تطبيق الفلتر</button>
            <button onClick={handleReset} style={buttonStyle("#64748b")}>إعادة الضبط</button>
          </div>
        </div>
      </div>

      {/* بطاقة الملخص */}
      <div className="row g-3 mb-4">
        <div className="col-md-12">
          <div style={summaryCardStyle}>
            <h6 style={summaryCardLabelStyle}>💸 إجمالي المصروفات</h6>
            <h3 style={summaryCardValueStyle}>{grandTotals.totalAmount?.toFixed(2)} ج.م</h3>
            <small style={{ color: "#64748b", fontSize: "13px" }}>({totalCount} مصروف)</small>
          </div>
        </div>
      </div>

      {/* الجدول */}
      <div className="table-responsive">
        <table className="table mb-0" style={tableStyle}>
          <thead style={{ background: "#dbeafe" }}>
            <tr>
              <th style={thStyle}>النوع</th>
              <th style={thStyle}>المبلغ</th>
              <th style={thStyle}>التاريخ</th>
              <th style={thStyle}>طريقة الدفع</th>
              <th style={thStyle}>ملاحظات</th>
              <th style={thStyle}>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length > 0 ? expenses.map((exp, idx) => (
              <tr key={exp.id} style={{ background: idx%2===0?"#f8fafc":"#ffffff" }}>
                <td style={{...tdStyle, fontWeight:600}}>
                  <span style={typeBadgeStyle}>
                    {getExpenseTypeLabel(exp.type)}
                  </span>
                </td>
                <td style={{...tdStyle, color:"#dc2626", fontWeight:700, fontSize:"20px"}}>
                  {exp.amount.toFixed(2)} ج.م
                </td>
<td style={{ ...tdStyle, fontSize: "17px" }}>
  {new Date(exp.date).toLocaleDateString()}
</td>

                <td style={{...tdStyle, fontWeight:600}}>
                  <span style={methodBadgeStyle(exp.method)}>
                    {getPaymentMethodLabel(exp.method)}
                  </span>
                </td>
                <td style={{...tdStyle, fontSize:"16px", maxWidth:"250px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {exp.notes || "—"}
                </td>
                <td style={tdStyle}>
                  <button style={actionBtnStyle("#f59e0b")} onClick={()=>navigate(`/expenses/${exp.id}/update`)}>
                    ✏️ تعديل
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="6" style={{ ...tdStyle, color: "#64748b", padding: "25px" }}>
                  لا توجد مصروفات
                  <div className="mt-3">
                    <button onClick={() => navigate("/create-expense")} style={buttonStyle("#22c55e")}>
                      إنشاء أول مصروف
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* الترقيم */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div style={{ color: "#64748b", fontSize: "14px" }}>الصفحة {currentPage} من {totalPages}</div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${!hasPrevious ? 'disabled':''}`}>
                <button onClick={()=>handlePageChange(currentPage-1)} style={{...paginationBtnStyle, cursor:!hasPrevious?"not-allowed":"pointer", opacity:!hasPrevious?0.5:1}}>السابق</button>
              </li>
              {getPageNumbers().map((page, idx)=>(
                <li key={idx}>
                  {page==='...' ? <span style={{...paginationBtnStyle, cursor:"default"}}>...</span> : 
                  <button onClick={()=>handlePageChange(page)} style={{...paginationBtnStyle, background: currentPage===page?"#2563eb":"#fff", color: currentPage===page?"#fff":"#475569", fontWeight: currentPage===page?"600":"500"}}>{page}</button>}
                </li>
              ))}
              <li className={`page-item ${!hasNext ? 'disabled':''}`}>
                <button onClick={()=>handlePageChange(currentPage+1)} style={{...paginationBtnStyle, cursor:!hasNext?"not-allowed":"pointer", opacity:!hasNext?0.5:1}}>التالي</button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

// ✅ أنماط
const thStyle = { color: "#1e40af", fontSize: "14px", padding: "12px 8px", fontWeight: "600", border: "1px solid #bfdbfe" };
const tdStyle = { padding: "12px 8px", border: "1px solid #e0e7ff", fontSize: "17px", color: "#475569", fontWeight: "600" };
const labelStyle = { color: "#475569", fontSize: "14px", fontWeight: "600", marginBottom: "6px", display: "block" };
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "15px", color: "#475569", outline: "none", cursor: "pointer", background: "#fff" };
const buttonStyle = (bgColor) => ({ background: bgColor, color: "#fff", padding: "10px 16px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "14px", cursor: "pointer", transition: "all 0.3s ease" });
const actionBtnStyle = (color) => ({ background: color, color: "#fff", padding: "6px 12px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "15px", cursor: "pointer", transition: "all 0.3s ease", whiteSpace: "nowrap" });
const paginationBtnStyle = { padding: "8px 14px", margin: "0 3px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "14px", cursor: "pointer", transition: "all 0.2s ease" };
const filterCardStyle = { background: "#fff", border: "1px solid #dbeafe", borderRadius: "8px", padding: "20px", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)" };
const summaryCardStyle = { background: "#fff", border: "2px solid #dc2626", borderRadius: "8px", padding: "20px", textAlign: "center", boxShadow: "0 4px 12px rgba(220, 38, 38, 0.2)" };
const summaryCardLabelStyle = { color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", marginBottom: "10px" };
const summaryCardValueStyle = { color: "#dc2626", fontSize: "28px", fontWeight: "700", marginBottom: "4px" };
const tableStyle = { border: "1px solid #dbeafe", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)", textAlign: "center" };
const typeBadgeStyle = { background: "#dbeafe", color: "#1e40af", padding: "6px 12px", borderRadius: "6px", fontSize: "16px", fontWeight: "600" };
const methodBadgeStyle = (method) => ({
  background: method==="Cash"?"#bbf7d0":method==="VodafoneCash"?"#fef3c7":"#e0e7ff",
  color: method==="Cash"?"#14532d":method==="VodafoneCash"?"#92400e":"#1e40af",
  padding: "6px 12px",
  borderRadius: "6px",
  fontSize: "16px",
  fontWeight: "600"
});